<script lang="ts">
import CKEditor from '@/app/common/components/CKEditor.vue';
import { LOG_CODE } from '@/app/const';
import { ComplaintActivityLog, ComplaintInfo } from '@/interface/Complaint.interface';
import { complaintService, userService } from '@/services/serviceProvider';
import { ref } from 'vue';

export default {
    components: {
        CKEditor
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            default: 't-complaint-chat'
        },
        cancelText: {
            type: String,
            default: 't-cancel'
        },
        confirmText: {
            type: String,
            default: 't-complaint-chat-send-message'
        },
        complaint: {
            type: Object,
            default: {}
        },
        isOwner: {
            type: Boolean,
            default: false,
        },
        viewMode: {
            type: Boolean,
            default: false
        },
        waitAirline: {
            type: Boolean,
            default: false,
        },
        filterDepartmentCode: {
            type: String,
            default: undefined
        },
        filterExternalDepartment: {
            type: String,
            default: undefined
        },
        isDpmChatFromListPage: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'onConfirm', 'onCancel'],
    setup() {
        const chatForm = ref();
        const userSv = userService;

        return { chatForm, userSv };
    },
    data() {
        return {
            request: this.complaint as ComplaintInfo,
            textEditorValue: '' as string,
            currentDepartment: userService.currentDepartment(),
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            }
        }
    },
    watch: {
        complaint: {
            handler(newVal, oldVal) {
                if (newVal) {
                    this.request = newVal;
                }
            }, deep: true
        },
    },
    computed: {
        dialogShow: {
            get() {
                return this.modelValue || false
            },
            set(newValue: boolean) {
                this.$emit('update:modelValue', newValue);
            }
        },
        allLogs() {
            const activitiesLogs = this.request.activitiesLogs || [];
            activitiesLogs.forEach(o => o.logType = 'activity');
            let chatLogs = this.request.chatLogs || [];
            if (this.filterDepartmentCode) {
                // หา external department ที่เกี่ยวข้องกับฝ่ายที่กำลังเปิดดู
                let externalDpm = undefined as string | undefined;
                if (this.request.relatedMainDepartment?.departmentCode === this.filterDepartmentCode) {
                    if (this.isDpmChatFromListPage) {
                        externalDpm = this.request.relatedMainDepartment.externalDepartment;
                    } else {
                        externalDpm = this.request.relatedMainDepartment.external?.externalDepartment;
                    }
                } else if (this.request.relatedSubDepartments?.find(o => o.departmentCode === this.filterDepartmentCode)) {
                    externalDpm = this.request.relatedSubDepartments?.find(o => o.departmentCode === this.filterDepartmentCode)?.external?.externalDepartment;
                }
                // หากได้ external department แล้วก็เอาไปกรอง chat ออกมาเฉพาะสายการบินที่เกี่ยวข้องกับฝ่ายที่กำลังเปิดดู
                // แก้เพิ่มเคส issue 350: กรณีที่ฝ่ายส่งไปหาสายการบิน 2 ครั้ง (คนละสายการบินเพราะสายการบินแรกส่งคืน) จะต้องเอา chat ของสายการบินแรกออกมาด้วย
                if (externalDpm) {
                    let currentEmail = userService.currentUserInfo()?.email;
                    chatLogs = chatLogs.filter(o => o.departmentCode === this.filterDepartmentCode ||
                        o.createBy === externalDpm || o.updatedBy == currentEmail);
                }
            }
            if (this.filterExternalDepartment) {
                // หา department code ที่เกี่ยวข้องกับสายการบินที่กำลังทำงานอยู่
                let departmentCode = undefined as string | undefined;
                if (this.isDpmChatFromListPage && this.request.relatedMainDepartment?.externalDepartment === this.filterExternalDepartment) {
                    departmentCode = this.request.relatedMainDepartment.departmentCode;
                } else if (this.request.relatedMainDepartment?.external?.externalDepartment === this.filterExternalDepartment) {
                    departmentCode = this.request.relatedMainDepartment.departmentCode;
                } else if (this.request.relatedSubDepartments?.find(o => o.external?.externalDepartment === this.filterExternalDepartment)) {
                    departmentCode = this.request.relatedSubDepartments?.find(o => o.external?.externalDepartment === this.filterExternalDepartment)?.departmentCode;
                }
                // หากได้ department code แล้วก็เอาไปกรอง chat ออกมาเฉพาะคนที่กำลังคุยกับสายการบินนี้
                if (departmentCode)
                    chatLogs = chatLogs.filter(o => o.departmentCode === departmentCode ||
                        o.createBy === userService.currentUserInfo()?.username);
            }
            chatLogs.forEach(o => o.logType = 'chat');
            return activitiesLogs.concat(chatLogs);
        },
        sortedAllLogs() {
            return this.allLogs.sort((a, b) => (a.createDate && b.createDate ? a.createDate < b.createDate ? -1 : 1 : 1));
        },
        canChat() {
            if (userService.isDpm() || (userService.isAsc() && this.currentResponsiblePerson == userService.currentUserInfo()?.employeeId)) {
                // ไม่ได้อยู่ใน Edit Mode แต่เป็นฝ่ายที่กำลังรอผลจากสายการบิน ต้องสามารถพูดคุยได้
                return (this.isOwner && !this.viewMode) || (this.isOwner && this.viewMode && this.waitAirline);
            } else if (userService.isAirline()) {
                return !this.viewMode;
            }
        },
        currentResponsiblePerson() {
            if (this.request.relatedMainDepartment?.departmentCode == userService.currentDepartment()) {
                if (this.isDpmChatFromListPage) {
                    let rp = this.request.relatedMainDepartment?.responiblePersons;
                    return rp && rp.length > 0 ? rp[0].employeeId : undefined;
                }
                return this.request.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible)?.employeeId;
            } else if (this.request.relatedSubDepartments?.find(s => s.departmentCode == userService.currentDepartment()) != null) {
                let sub = this.request.relatedSubDepartments.find(s => s.departmentCode == userService.currentDepartment());
                if (this.isDpmChatFromListPage) {
                    let rp = sub?.responiblePersons;
                    return rp && rp.length > 0 ? rp[0].employeeId : undefined;
                }
                return sub?.responiblePersons?.find(o => o.activeResponsible)?.employeeId;
            }
        }
    },
    methods: {
        async confirm() {
            const valid = await this.chatForm.validate();
            if (valid.valid) {
                const newChat = {
                    departmentCode: userService.isAirline() ? undefined : userService.currentDepartment(),
                    detail: this.textEditorValue,
                    createBy: userService.currentUserInfo()?.username,
                    responsibleName: userService.isAirline() ? userService.currentUserInfo()?.displayName : userService.currentUserInfo()?.displayName,
                    createDate: new Date(),
                } as ComplaintActivityLog;
                // this.dialogShow = false;
                this.$emit('onConfirm', newChat);
                this.textEditorValue = '';
            }
        },
        cancel() {
            this.dialogShow = false;
            this.$emit('onCancel')
        },
        logDateString(date: Date | undefined) {
            return complaintService.dateTimeString(date);
        },
        editorChatChange(value: any) {
            this.textEditorValue = value;
        },
        decodeLog(detail: string) {
            for (const [key, value] of Object.entries(LOG_CODE)) {
                if (detail.includes(value)) {
                    const translate = this.$t(`t-log-${value.replace('{', '').replace('}', '')}`);
                    detail = detail.replace(value, translate);
                }
            }
            return detail;
        },
    }
}
</script>

<template>
    <v-dialog v-model="dialogShow" width="60%" min-height="90%" :close-on-back="false" persistent class="chat-dialog">
        <v-form ref="chatForm" class="admin-form app-form-input chat-form h-100" validate-on="submit lazy"
            @submit.prevent="confirm">
            <v-card class="dialog-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t(header) }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn icon="mdi-close" @click="cancel"></v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-divider class="mx-4"></v-divider>
                <v-card-text v-if="sortedAllLogs && sortedAllLogs.length > 0" class="chat-view">
                    <div v-for="chat in sortedAllLogs" class="d-flex w-100 mb-2">
                        <div v-if="chat.logType === 'chat'" class="d-flex w-100">
                            <div v-if="chat.createBy !== userSv.currentUserInfo()?.username" class="chat-avatar mr-2">
                                <v-avatar v-if="chat.departmentCode" color="primary">
                                    {{ chat.departmentCode }}
                                </v-avatar>
                                <v-avatar v-else color="primary">
                                    {{ chat.createBy?.substring(0, 2).toUpperCase() }}
                                </v-avatar>
                            </div>
                            <div class="chat-content">
                                <v-card class="inner-card" style="border-radius: 24px !important;">
                                    <v-card-text class="formatted"
                                        style="padding-bottom: 0 !important; padding-top: 5px !important;">
                                        <div v-html="chat.detail"></div>
                                    </v-card-text>
                                    <v-divider></v-divider>
                                    <v-card-text>
                                        <span class="mr-2">{{ chat.responsibleName }}</span>
                                        <span v-if="chat.departmentCode" class="mr-2 dotted">
                                            {{ $t('t-department') }} {{ chat.departmentCode }}
                                        </span>
                                        <span class="dotted">{{ logDateString(chat.createDate) }} {{ $t('t-time-unit')
                                            }}</span>
                                    </v-card-text>
                                </v-card>
                            </div>
                            <div v-if="chat.createBy === userSv.currentUserInfo()?.username"
                                class="ml-2 mt-1 chat-avatar">
                                <v-avatar v-if="chat.departmentCode" color="primary">
                                    {{ chat.departmentCode }}
                                </v-avatar>
                                <v-avatar v-else color="primary">
                                    {{ chat.createBy?.substring(0, 2).toUpperCase() }}
                                </v-avatar>
                            </div>
                        </div>
                        <div v-else class="d-flex align-center justify-center w-100 py-2"
                            style="color:#C7CFDB; font-size: 14px;">
                            <span class="mr-2" style="min-width: fit-content;">
                                {{ logDateString(chat.createDate) }} {{ $t('t-time-unit') }} {{ chat.createByName }}
                            </span>
                            <span v-if="chat.departmentCode" class="mr-2" style="min-width: fit-content;">
                                ({{ $t('t-department') }} {{ chat.departmentCode }})
                            </span>
                            <span>{{ decodeLog(chat.detail) }}</span>
                        </div>
                    </div>
                </v-card-text>
                <v-card-text v-else>
                    {{ $t('t-complaint-chat-no-data') }}
                </v-card-text>
                <v-card-text v-if="canChat" class="formatted">
                    <CKEditor id="chatEditor" :data="textEditorValue" @onChange="editorChatChange">
                    </CKEditor>
                </v-card-text>
                <v-card-actions v-if="canChat">
                    <v-container class="mx-0">
                        <div class="d-flex justify-end">
                            <v-btn @click="cancel" variant="outlined" color="primary" size="large">
                                {{ $t(cancelText) }}
                            </v-btn>
                            <v-btn @click="confirm" variant="flat" color="primary" size="large"
                                :disabled="textEditorValue === undefined || textEditorValue === null || textEditorValue === ''">
                                {{ $t(confirmText) }}
                            </v-btn>
                        </div>
                    </v-container>
                </v-card-actions>
                <v-card-text
                    v-if="(userSv.isDpm() || (userSv.isAsc() && currentResponsiblePerson == userSv.currentUserInfo()?.employeeId)) && !canChat">
                    <v-alert v-if="!isOwner" type="info" variant="tonal">
                        {{ $t('t-complaint-cannot-chat-not-owner') }}
                    </v-alert>
                    <v-alert v-else type="info" variant="tonal">{{ $t('t-complaint-cannot-chat-ended') }}</v-alert>
                </v-card-text>
                <v-card-text v-if="userSv.isAirline() && !canChat">
                    <v-alert type="info" variant="tonal">{{ $t('t-complaint-cannot-chat-ended') }}</v-alert>
                </v-card-text>
            </v-card>
        </v-form>
    </v-dialog>
</template>